import { forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  aivorRecordDisplay,
  aivorRecordIdFor,
  fullDecisionBody,
  isAivorService,
  isKeptOutOfSage,
  isSageService,
  isSyncDisabledError,
  ItemReportGroup,
  parseSageSnapshot,
  sageErrorMessageFor,
  SageEntityTypeName,
  SageReconciliationMatch,
  sageSnapshotName,
} from '../../../../shared/services/sage-integration.model';
import { SageIntegrationService } from '../../../../shared/services/sage-integration.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ErrorGroup, ErrorRecordRow } from '../dashboard/errors-column.component';
import { CategoryFixState } from './category-fix.state';
import { CodeFixState } from './code-fix.state';

const SALES = 1;

/** The items behind one record, for a sales group of their own. A code isn't unique, and the
 * read-time check counts every item with it, so all of them change together. */
export interface SalesItemRow {
  recordId: number;
  itemIds: number[];
  name: string;
  code: string;
  groupId: number | null;
  usesDefault: boolean;
  saving: boolean;
  error: string | null;
}

/** One record of a wrong match: the Sage record still waiting in a review, and the Aivor record it was matched to. */
export interface RematchRow {
  record: ErrorRecordRow;
  type: SageEntityTypeName;
  match: SageReconciliationMatch | null;
  sageName: string;
  sageCode: string;
  sageKind: string | null;
  aivorName: string;
  aivorCode: string;
  aivorKind: string | null;
  /** confirm-selected applies items and suppliers one by one; other types are applied on their matching screen. */
  canAddAsNew: boolean;
  saving: boolean;
  added: boolean;
  error: string | null;
}

interface TypeData {
  sessionId: number | null;
  matches: SageReconciliationMatch[];
  aivorRecords: any[];
}

/**
 * What the open reason's fix needs, loaded once and shared by the web panel and the phone sheet
 * (the console renders both). The console answers `errorsChanged$` by reloading the list.
 */
export class SageErrorFixStore {
  group: ErrorGroup | null = null;

  reportGroups: ItemReportGroup[] | null = null;
  /** The organisation's default sales group, which outlives the open reason; the console sets it from the settings. */
  defaultGroupId: number | null = null;
  loadingGroups = false;
  savingDefault = false;
  refreshing = false;
  refreshResult: string | null = null;

  rematchRows: RematchRow[] = [];
  loadingRematch = false;
  rematchLoadError = false;

  salesItems: SalesItemRow[] = [];
  loadingSalesItems = false;

  readonly codes: CodeFixState;
  readonly categories: CategoryFixState;

  /** Something was saved from this panel, so the reason's sync is worth offering even before it clears. */
  changesSaved = false;

  error: string | null = null;

  readonly errorsChanged$ = new Subject<void>();
  readonly groupsChanged$ = new Subject<void>();
  readonly syncDisabled$ = new Subject<void>();

  private typeData = new Map<SageEntityTypeName, TypeData>();
  private rowState = new Map<number, Pick<RematchRow, 'saving' | 'added' | 'error'>>();
  // Up on every close, so a reply for a reason no longer open still reloads the list and says what
  // happened, but leaves the panel (now another reason's) alone.
  private epoch = 0;

  constructor(private sage: SageIntegrationService, private toast: ToastService) {
    this.codes = new CodeFixState(sage, (current) => this.savedChange(current));
    this.categories = new CategoryFixState(
      sage,
      (message) => {
        if (message) toast.success(message);
        this.errorsChanged$.next();
      },
      () => this.syncDisabled$.next()
    );
  }

  open(group: ErrorGroup): void {
    this.close();
    this.group = group;
    if (group.fix === 'sales-group' || group.fix === 'refresh-groups') {
      this.loadGroups();
      this.loadSalesItems();
    }
    if (group.fix === 'code' || group.fix === 'name') this.codes.load(group, group.fix);
    if (group.fix === 'categories') this.categories.load(group);
    if (group.fix === 'rematch') this.loadRematch();
  }

  private savedChange(current = true): void {
    if (current) this.changesSaved = true;
    this.errorsChanged$.next();
  }

  /** The same reason after the list reloaded: its records may have changed, what was loaded stays. */
  follow(group: ErrorGroup): void {
    this.group = group;
    if (group.fix === 'rematch' && !this.loadingRematch) this.buildRematchRows();
  }

  close(): void {
    this.epoch++;
    this.group = null;
    this.reportGroups = null;
    this.loadingGroups = this.savingDefault = this.refreshing = false;
    this.refreshResult = null;
    this.rematchRows = [];
    this.loadingRematch = this.rematchLoadError = false;
    this.salesItems = [];
    this.loadingSalesItems = false;
    this.changesSaved = false;
    this.error = null;
    this.typeData.clear();
    this.rowState.clear();
    this.codes.reset();
    this.categories.reset();
  }

  // ---- A sales group per item ----

  private loadSalesItems(): void {
    const seen = new Set<string>();
    const records = (this.group?.records ?? []).filter((r) => r.recordId && !seen.has(r.recordId.trim().toLowerCase()) && seen.add(r.recordId.trim().toLowerCase()));
    this.loadingSalesItems = true;
    const at = this.epoch;
    this.sage.getAivorRecords('Item').subscribe({
      next: (items) => {
        if (at !== this.epoch) return;
        this.loadingSalesItems = false;
        const same = (a: string | null | undefined, b: string) => !!a && a.trim().toLowerCase() === b.trim().toLowerCase();
        this.salesItems = records
          .map((r) => ({ record: r, matches: (items ?? []).filter((i) => same(i.itemCode, r.recordId ?? '')) }))
          .filter(({ matches }) => matches.length)
          .map(({ record, matches }) => ({
            recordId: record.id,
            itemIds: matches.map((i) => i.itemID),
            name: record.name,
            code: record.recordId ?? '',
            groupId: null,
            usesDefault: false,
            saving: false,
            error: null,
          }));
      },
      error: () => {
        if (at !== this.epoch) return;
        this.loadingSalesItems = false;
        this.salesItems = [];
      },
    });
  }

  /** Gives the items their own sales group, or null for the default; Sage must know it, which the server checks too. */
  setItemGroup(row: SalesItemRow, groupId: number | null): void {
    if (row.saving) return;
    this.setSalesRow(row, { saving: true, error: null });
    const at = this.epoch;
    this.sage.setItemsSalesGroup(row.itemIds, groupId).subscribe({
      next: () => {
        if (at === this.epoch) this.setSalesRow(row, { saving: false, groupId, usesDefault: groupId === null });
        this.savedChange(at === this.epoch);
      },
      error: (err) => {
        if (at !== this.epoch) return;
        this.setSalesRow(row, {
          saving: false,
          error: ((err?.status === 400 || err?.status === 404) && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't save the sales group. Try again.",
        });
      },
    });
  }

  private setSalesRow(row: SalesItemRow, change: Partial<SalesItemRow>): void {
    this.salesItems = this.salesItems.map((r) => (r.recordId === row.recordId ? { ...r, ...change } : r));
  }

  // ---- Sales groups ----

  /** Groups Sage can record items' sales under: the only ones the server accepts as the default. */
  get usableSalesGroups(): ItemReportGroup[] {
    return (this.reportGroups ?? []).filter((g) => g.isActive && g.itemReportingGroupTypeId === SALES && g.sageItemReportingGroupId != null);
  }

  get usableDefault(): ItemReportGroup | null {
    return this.usableSalesGroups.find((g) => g.id === this.defaultGroupId) ?? null;
  }

  get unsyncedSalesGroups(): ItemReportGroup[] {
    return (this.reportGroups ?? []).filter((g) => g.isActive && g.itemReportingGroupTypeId === SALES && g.sageItemReportingGroupId == null);
  }

  private loadGroups(): void {
    this.loadingGroups = true;
    const at = this.epoch;
    this.sage.getItemReportGroups().subscribe({
      next: (groups) => {
        if (at !== this.epoch) return;
        this.loadingGroups = false;
        this.reportGroups = groups ?? [];
      },
      error: () => {
        if (at !== this.epoch) return;
        this.loadingGroups = false;
        this.reportGroups = [];
        this.error = "Couldn't load the sales groups. Close this and try again.";
      },
    });
  }

  setDefault(groupId: number): void {
    if (this.savingDefault) return;
    const name = this.reportGroups?.find((g) => g.id === groupId)?.description ?? 'That group';
    this.savingDefault = true;
    this.error = null;
    const at = this.epoch;
    this.sage.setDefaultSalesReportingGroup(groupId).subscribe({
      next: () => {
        if (at === this.epoch) this.savingDefault = false;
        this.toast.success(`${name} is now your default sales group.`);
        this.defaultGroupId = groupId;
        this.errorsChanged$.next();
      },
      error: (err) => {
        if (at !== this.epoch) return;
        this.savingDefault = false;
        this.error = (err?.status === 400 && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't save the sales group. Try again.";
      },
    });
  }

  refreshGroups(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.refreshResult = null;
    this.error = null;
    const at = this.epoch;
    this.sage.refreshItemReportGroups().subscribe({
      next: (res) => {
        if (at === this.epoch) {
          this.refreshing = false;
          this.refreshResult = `${res.count} ${res.count === 1 ? 'group' : 'groups'} read from Sage.`;
          this.loadGroups();
        }
        this.groupsChanged$.next();
        this.errorsChanged$.next();
      },
      error: (err) => {
        if (isSyncDisabledError(err)) this.syncDisabled$.next();
        if (at !== this.epoch) return;
        this.refreshing = false;
        this.error = sageErrorMessageFor(err) ?? "Couldn't refresh the groups from Sage. Try again.";
      },
    });
  }

  // ---- Wrong matches ----

  private loadRematch(): void {
    const types = [...new Set((this.group?.records ?? []).map((r) => r.entityType as SageEntityTypeName))];
    if (!types.length) return;
    this.loadingRematch = true;
    // No open review for a type is a 404: none of its records can still be waiting.
    const session = (type: SageEntityTypeName) =>
      this.sage.getSession(type).pipe(catchError((err) => (err?.status === 404 ? of(null) : throwError(() => err))));
    const load = (type: SageEntityTypeName): Observable<[SageEntityTypeName, TypeData]> =>
      forkJoin({ review: session(type), records: this.sage.getAivorRecords(type).pipe(catchError(() => of(null))) }).pipe(
        map(({ review, records }) => [type, { sessionId: review?.session.id ?? null, matches: review?.matches ?? [], aivorRecords: records ?? [] }])
      );
    const at = this.epoch;
    forkJoin(types.map(load)).subscribe({
      next: (loaded) => {
        if (at !== this.epoch) return;
        this.loadingRematch = false;
        this.typeData = new Map(loaded);
        this.buildRematchRows();
      },
      error: () => {
        if (at !== this.epoch) return;
        this.loadingRematch = false;
        this.rematchLoadError = true;
      },
    });
  }

  // A link problem's record id is the Aivor id the Sage record was matched to (SageOpenProblemCheck).
  private buildRematchRows(): void {
    this.rematchRows = (this.group?.records ?? []).map((record) => {
      const type = record.entityType as SageEntityTypeName;
      const data = this.typeData.get(type);
      const aivorId = Number(record.recordId);
      const match = data?.matches.find((m) => (m.resolvedAivorRecordId ?? m.aivorRecordId) === aivorId) ?? null;
      const raw = parseSageSnapshot(match?.sageRecordSnapshot);
      const aivor = data?.aivorRecords.find((r) => aivorRecordIdFor(type, r) === aivorId);
      const shown = aivor ? aivorRecordDisplay(type, aivor) : null;
      const items = type === 'Item' || type === 'ItemBundle';
      return {
        record,
        type,
        match,
        sageName: match ? sageSnapshotName(match.sageRecordSnapshot, type) : record.name,
        sageCode: raw?.Code ?? raw?.BundleCode ?? '',
        sageKind: items && raw ? (isSageService(raw) ? 'Service' : 'Stock item') : null,
        aivorName: shown?.name ?? 'No longer in Aivor',
        aivorCode: shown?.meta ?? '',
        aivorKind: items && aivor ? (isKeptOutOfSage(aivor) ? 'Variable item' : isAivorService(aivor) ? 'Service' : 'Stock item') : null,
        canAddAsNew: !!match && data?.sessionId != null && (type === 'Item' || type === 'Supplier'),
        saving: false,
        added: false,
        error: null,
        ...this.rowState.get(record.id),
      };
    });
  }

  /** Marks the Sage record to be added to Aivor as new, then applies just that record. */
  addAsNew(row: RematchRow): void {
    const sessionId = this.typeData.get(row.type)?.sessionId;
    const match = row.match;
    if (!match || sessionId == null || row.saving) return;
    this.setRow(row, { saving: true, error: null });
    const at = this.epoch;
    this.sage
      .updateMatchDecision(sessionId, match.id, fullDecisionBody(match, { decision: 'ImportToAivor', resolvedAivorRecordId: null }))
      .pipe(switchMap(() => this.sage.confirmSelectedMatches(sessionId, [match.id])))
      .subscribe({
        next: (result) => {
          const blocked = result.blocked.find((b) => b.matchId === match.id);
          if (blocked) {
            if (at === this.epoch) this.setRow(row, { saving: false, error: blocked.message });
            return;
          }
          if (at === this.epoch) this.setRow(row, { saving: false, added: true });
          this.toast.success(`${row.sageName} added to Aivor.`);
          this.errorsChanged$.next();
        },
        error: (err) => {
          if (isSyncDisabledError(err)) this.syncDisabled$.next();
          if (at !== this.epoch) return;
          this.setRow(row, {
            saving: false,
            error: (err?.status === 409 && err?.error?.message) || sageErrorMessageFor(err) || "Couldn't add it. Try again.",
          });
        },
      });
  }

  private setRow(row: RematchRow, change: Partial<Pick<RematchRow, 'saving' | 'added' | 'error'>>): void {
    const next = { saving: row.saving, added: row.added, error: row.error, ...this.rowState.get(row.record.id), ...change };
    this.rowState.set(row.record.id, next);
    this.rematchRows = this.rematchRows.map((r) => (r.record.id === row.record.id ? { ...r, ...next } : r));
  }
}
